// Client Portal — Email Intelligence portal (M365 / Gmail / IMAP email
// drafting). Extracted from App.js; depends only on lib/api.
import { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import { emailIntelFetch } from "../lib/api";
import { EmailIntelSettings } from "./EmailIntelSettings";
import { EmailReviewConsole } from "./EmailReviewConsole";

const VERTICALS = [
  {
    id: "construction",
    label: "Construction",
    desc: "Bid inquiries, invoices, subcontractor comms",
    icon: "🏗️",
  },
  {
    id: "insurance",
    label: "Insurance",
    desc: "Claims, policy inquiries, renewals",
    icon: "🛡️",
  },
];

export function EmailIntelligencePortal({ facility, user, pendingCid }) {
  const [step, setStep] = useState(1);
  const [connection, setConnection] = useState(null);
  const [cid, setCid] = useState(pendingCid || null);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailboxes, setSelectedMailboxes] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState(null);
  const [titanForm, setTitanForm] = useState({
    open: false,
    email: "",
    password: "",
  });

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect this facility from Email Intelligence? This will remove all mailbox subscriptions and routing rules.",
      )
    )
      return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await emailIntelFetch(`/connections/${facility.facility_id}`, {
        method: "DELETE",
      });
      setConnection(null);
      setCid(null);
      setStep(1);
    } catch (e) {
      setDisconnectError("Disconnect failed. Please try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [addingRule, setAddingRule] = useState(false);
  const [newRuleDraft, setNewRuleDraft] = useState({
    priority: 100,
    subject: "",
    body: "",
    from: "",
    to: "",
    from_in_list: "",
  });

  const [senderLists, setSenderLists] = useState([]);
  const [senderListsLoading, setSenderListsLoading] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [listAddressDraft, setListAddressDraft] = useState("");
  const [addingList, setAddingList] = useState(false);
  const [newListDraft, setNewListDraft] = useState({ name: "", addresses: "" });

  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const data = await emailIntelFetch(
        `/rules?facility_id=${facility.facility_id}`,
      );
      setRules(data.rules || []);
    } catch (e) {
      // silent
    } finally {
      setRulesLoading(false);
    }
  };

  const toggleRule = async (id, enabled) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled } : r)));
    await emailIntelFetch(`/rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }).catch(() => loadRules());
  };

  const saveRule = async () => {
    await emailIntelFetch(`/rules/${editingRule}`, {
      method: "PATCH",
      body: JSON.stringify({
        priority: Number(editDraft.priority),
        conditions: buildConditions(editDraft),
      }),
    });
    setEditingRule(null);
    loadRules();
  };

  const createRule = async () => {
    await emailIntelFetch(`/rules`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: facility.facility_id,
        priority: Number(newRuleDraft.priority),
        conditions: buildConditions(newRuleDraft),
      }),
    });
    setAddingRule(false);
    setNewRuleDraft({ priority: 100, subject: "", body: "", from: "", to: "" });
    loadRules();
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this routing rule?")) return;
    await emailIntelFetch(`/rules/${id}`, { method: "DELETE" });
    loadRules();
  };

  const loadSenderLists = async () => {
    setSenderListsLoading(true);
    try {
      const data = await emailIntelFetch(
        `/sender-lists?facility_id=${facility.facility_id}`,
      );
      setSenderLists(data.lists || []);
    } catch (e) {
      // silent
    } finally {
      setSenderListsLoading(false);
    }
  };

  const startEditList = async (name) => {
    setEditingList(name);
    try {
      const data = await emailIntelFetch(
        `/sender-lists/${encodeURIComponent(name)}?facility_id=${facility.facility_id}`,
      );
      setListAddressDraft((data.addresses || []).join("\n"));
    } catch (e) {
      setListAddressDraft("");
    }
  };

  const saveSenderList = async (name) => {
    const addresses = listAddressDraft
      .split(/[\n,]+/)
      .map((a) => a.trim())
      .filter(Boolean);
    await emailIntelFetch(`/sender-lists/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify({ facility_id: facility.facility_id, addresses }),
    });
    setEditingList(null);
    loadSenderLists();
  };

  const createSenderList = async () => {
    const addresses = newListDraft.addresses
      .split(/[\n,]+/)
      .map((a) => a.trim())
      .filter(Boolean);
    await emailIntelFetch(
      `/sender-lists/${encodeURIComponent(newListDraft.name.trim())}`,
      {
        method: "PUT",
        body: JSON.stringify({ facility_id: facility.facility_id, addresses }),
      },
    );
    setAddingList(false);
    setNewListDraft({ name: "", addresses: "" });
    loadSenderLists();
  };

  const deleteSenderList = async (name) => {
    if (!window.confirm(`Delete list "${name}"?`)) return;
    await emailIntelFetch(
      `/sender-lists/${encodeURIComponent(name)}?facility_id=${facility.facility_id}`,
      { method: "DELETE" },
    );
    loadSenderLists();
  };

  const parseList = (str) =>
    (str || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  const joinList = (arr) => (arr || []).join(", ");

  const conditionSummary = (conditions) => {
    if (!conditions || Object.keys(conditions).length === 0)
      return "All emails (catch-all)";
    const parts = [];
    if (conditions.subject_contains?.length)
      parts.push(`Subject: ${conditions.subject_contains.join(", ")}`);
    if (conditions.body_contains?.length)
      parts.push(`Body: ${conditions.body_contains.join(", ")}`);
    if (conditions.from_address?.length)
      parts.push(`From: ${conditions.from_address.join(", ")}`);
    if (conditions.to_address?.length)
      parts.push(`To: ${conditions.to_address.join(", ")}`);
    if (conditions.from_in_list)
      parts.push(`From list: ${conditions.from_in_list}`);
    return parts.join(" • ") || "All emails (catch-all)";
  };

  const buildConditions = (draft) => {
    const c = {};
    const subject = parseList(draft.subject);
    const body = parseList(draft.body);
    const from = parseList(draft.from);
    const to = parseList(draft.to);
    if (subject.length) c.subject_contains = subject;
    if (body.length) c.body_contains = body;
    if (from.length) c.from_address = from;
    if (to.length) c.to_address = to;
    if (draft.from_in_list) c.from_in_list = draft.from_in_list;
    return c;
  };

  // Load connection on mount
  useEffect(() => {
    emailIntelFetch(`/connections/${facility.facility_id}`)
      .then((data) => {
        const conn = data.connection;
        setConnection(conn);
        if (pendingCid) {
          setCid(pendingCid);
          setStep(3);
          loadMailboxes(pendingCid);
        } else if (!conn || conn.status === "pending_consent") {
          setStep(1);
        } else if (conn.status === "connected") {
          setCid(conn.id);
          setStep(3);
          loadMailboxes(conn.id);
        } else if (conn.status === "live") {
          setConnection(conn);
          setStep(5);
        }
      })
      .catch(() => setStep(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility.facility_id]);

  const loadMailboxes = async (connectionId) => {
    setLoading(true);
    try {
      const data = await emailIntelFetch(
        `/mailboxes/discover?cid=${connectionId}`,
      );
      setMailboxes(data.mailboxes || []);
    } catch (e) {
      setError("Failed to load mailboxes. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerticalSelect = async (vertical) => {
    setSelectedVertical(vertical);
    setLoading(true);
    setError(null);
    try {
      const data = await emailIntelFetch(`/connections/initialize`, {
        method: "POST",
        body: JSON.stringify({
          facility_id: facility.facility_id,
          industry: vertical,
        }),
      });
      setCid(data.connection_id);
      setStep(2);
    } catch (e) {
      setError("Failed to initialize connection. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectM365 = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await emailIntelFetch(
        `/m365/connect-url?facility_id=${facility.facility_id}`,
      );
      window.location.href = data.consent_url;
    } catch (e) {
      setError("Could not generate consent URL. Please contact support.");
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await emailIntelFetch(
        `/gmail/connect-url?facility_id=${facility.facility_id}`,
      );
      window.location.href = data.consent_url;
    } catch (e) {
      setError("Could not generate consent URL. Please contact support.");
      setLoading(false);
    }
  };

  const handleConnectTitan = async () => {
    if (!titanForm.email.trim() || !titanForm.password) {
      setError("Enter your TitanMail email address and password.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await emailIntelFetch(`/imap/connect`, {
        method: "POST",
        body: JSON.stringify({
          facility_id: facility.facility_id,
          email: titanForm.email.trim(),
          password: titanForm.password,
          preset: "titan",
        }),
      });
      setCid(data.connection_id);
      setTitanForm({ open: false, email: "", password: "" });
      setStep(3);
      loadMailboxes(data.connection_id);
    } catch (e) {
      setError(
        "TitanMail login failed. Check the email address and password, then try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMailboxes = async () => {
    if (selectedMailboxes.length === 0) {
      setError("Select at least one mailbox.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await emailIntelFetch(`/mailboxes/confirm`, {
        method: "POST",
        body: JSON.stringify({ cid, mailbox_ids: selectedMailboxes }),
      });
      await emailIntelFetch(`/go-live`, {
        method: "POST",
        body: JSON.stringify({
          cid,
          vertical: selectedVertical || "construction",
        }),
      });
      setStep(4);
      pollForLive();
    } catch (e) {
      setError("Failed to confirm mailboxes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const pollForLive = () => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const data = await emailIntelFetch(
          `/connections/${facility.facility_id}`,
        );
        if (data.connection?.status === "live") {
          clearInterval(interval);
          setConnection(data.connection);
          setStep(5);
        } else if (data.connection?.status === "error") {
          clearInterval(interval);
          setError("Go-live failed. Please retry.");
        }
      } catch {}
      if (attempts > 40) clearInterval(interval);
    }, 3000);
  };

  const toggleMailbox = (id) => {
    setSelectedMailboxes((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  };

  // Step 1: Vertical selection
  if (step === 1)
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h2 className="text-xl font-bold text-slate-800 mb-2">
          Email Intelligence Setup
        </h2>
        <p className="text-slate-500 mb-6">
          Select your industry to pre-configure routing rules and reply
          templates.
        </p>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {VERTICALS.map((v) => (
            <button
              key={v.id}
              onClick={() => handleVerticalSelect(v.id)}
              disabled={loading}
              className="p-6 rounded-xl border-2 border-orange-200 bg-orange-50 hover:border-orange-400 hover:shadow-md transition-all text-left"
            >
              <span className="text-3xl">{v.icon}</span>
              <h3 className="font-semibold text-orange-800 mt-2">{v.label}</h3>
              <p className="text-xs text-slate-500 mt-1">{v.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );

  // Step 2: Connect email provider
  if (step === 2)
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
            <Mail className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Connect Your Email
          </h2>
          <p className="text-slate-500 text-sm">
            HyveAI only reads incoming mail and writes reply drafts — it never
            sends email on your behalf.
          </p>
        </div>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="space-y-3">
          <div className="bg-white rounded-xl border-2 border-slate-200 hover:border-orange-300 transition-colors p-5 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Microsoft 365</h3>
              <p className="text-xs text-slate-500 mt-1">
                Organization-wide connection. Requires M365 Global Admin or
                Exchange Admin to approve.
              </p>
            </div>
            <button
              onClick={handleConnectM365}
              disabled={loading}
              className="ml-4 shrink-0 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? "..." : "Connect →"}
            </button>
          </div>
          <div className="bg-white rounded-xl border-2 border-slate-200 hover:border-orange-300 transition-colors p-5 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Gmail</h3>
              <p className="text-xs text-slate-500 mt-1">
                Connect one Gmail or Google Workspace mailbox. You'll sign in
                with Google and approve access.
              </p>
            </div>
            <button
              onClick={handleConnectGmail}
              disabled={loading}
              className="ml-4 shrink-0 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? "..." : "Connect →"}
            </button>
          </div>
          <div className="bg-white rounded-xl border-2 border-slate-200 hover:border-orange-300 transition-colors p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">TitanMail</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Connect a Titan mailbox with its email address and password.
                </p>
              </div>
              {!titanForm.open && (
                <button
                  onClick={() => setTitanForm((f) => ({ ...f, open: true }))}
                  disabled={loading}
                  className="ml-4 shrink-0 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  Connect →
                </button>
              )}
            </div>
            {titanForm.open && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={titanForm.email}
                    onChange={(e) =>
                      setTitanForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="you@yourdomain.com"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={titanForm.password}
                    onChange={(e) =>
                      setTitanForm((f) => ({ ...f, password: e.target.value }))
                    }
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Stored encrypted. Used only to read incoming mail and save
                    reply drafts via IMAP (imap.titan.email).
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleConnectTitan}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Verifying..." : "Verify & Connect"}
                  </button>
                  <button
                    onClick={() =>
                      setTitanForm({ open: false, email: "", password: "" })
                    }
                    disabled={loading}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );

  // Step 3: Mailbox selection
  if (step === 3)
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h2 className="text-xl font-bold text-slate-800 mb-2">
          Select Mailboxes to Monitor
        </h2>
        <p className="text-slate-500 mb-6 text-sm">
          Only emails received in these mailboxes will trigger AI drafts.
        </p>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        {loading ? (
          <div className="text-center py-12 text-slate-400">
            Loading mailboxes...
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-3">
              {mailboxes.length === 0 && (
                <p className="text-sm text-slate-400">No mailboxes found.</p>
              )}
              {mailboxes.map((mb) => (
                <label
                  key={mb.mailbox_id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMailboxes.includes(mb.mailbox_id)}
                    onChange={() => toggleMailbox(mb.mailbox_id)}
                    className="rounded"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {mb.display_name}
                    </p>
                    <p className="text-xs text-slate-400">{mb.mail_address}</p>
                  </div>
                </label>
              ))}
            </div>
            <button
              onClick={handleConfirmMailboxes}
              disabled={loading || selectedMailboxes.length === 0}
              className="w-full py-3 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              Confirm & Go Live ({selectedMailboxes.length} selected)
            </button>
          </>
        )}
      </div>
    );

  // Step 4: Provisioning
  if (step === 4)
    return (
      <div className="p-6 max-w-xl mx-auto text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center animate-pulse">
          <Mail className="w-8 h-8 text-orange-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Going Live...</h2>
        <p className="text-slate-500 text-sm">
          Setting up subscriptions and knowledge base. This takes about 30
          seconds.
        </p>
        {error && (
          <div className="mt-4">
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
            <button
              onClick={() => {
                setError(null);
                setStep(3);
              }}
              className="px-4 py-2 rounded-lg bg-orange-100 text-orange-700 text-sm font-medium"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );

  // Step 5: Live portal
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <Mail className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Email Intelligence
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
            Live
          </span>
        </div>
      </div>

      <div className="flex space-x-1 mb-6 border-b border-slate-200">
        {["overview", "console", "routing", "drafts", "eval"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === "routing") {
                if (rules.length === 0 && !rulesLoading) loadRules();
                if (senderLists.length === 0 && !senderListsLoading)
                  loadSenderLists();
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? "border-orange-500 text-orange-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab === "overview"
              ? "Overview"
              : tab === "console"
                ? "Review Console"
                : tab === "routing"
                  ? "Routing Rules"
                  : tab === "drafts"
                    ? "Recent Drafts"
                    : "Eval Engine"}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-700 mb-3">Connection</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Platform</span>
                <span className="font-medium text-slate-700">
                  {connection?.platform === "gmail"
                    ? "Gmail"
                    : connection?.platform === "imap"
                      ? "TitanMail (IMAP)"
                      : "Microsoft 365"}
                </span>
              </div>
              {connection?.platform === "gmail" ||
              connection?.platform === "imap" ? (
                <div className="flex justify-between">
                  <span className="text-slate-500">Mailbox</span>
                  <span className="font-mono text-xs text-slate-600">
                    {connection?.mailbox_address || "—"}
                  </span>
                </div>
              ) : (
                <div className="flex justify-between">
                  <span className="text-slate-500">Tenant</span>
                  <span className="font-mono text-xs text-slate-600">
                    {connection?.m365_tenant_id || "—"}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                  Live
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Connected</span>
                <span className="text-slate-600">
                  {connection?.consented_at
                    ? new Date(connection.consented_at).toLocaleDateString()
                    : "—"}
                </span>
              </div>
            </div>
            {disconnectError && (
              <p className="mt-2 text-xs text-red-600">{disconnectError}</p>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-700 mb-2">How it works</h3>
            <ol className="text-sm text-slate-500 space-y-1 list-decimal list-inside">
              <li>New emails arrive in monitored mailboxes</li>
              <li>AI searches your knowledge base for relevant content</li>
              <li>A draft reply appears in the mailbox's Drafts folder</li>
              <li>Your team reviews, edits, and sends</li>
            </ol>
          </div>
        </div>
      )}

      {activeTab === "routing" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Rules are evaluated in priority order. Lower number = higher
              priority.
            </p>
            <button
              onClick={() => {
                if (rules.length === 0 && !rulesLoading) loadRules();
                setAddingRule(true);
              }}
              className="text-xs px-3 py-1.5 rounded-lg bg-orange-100 text-orange-700 hover:bg-orange-200 font-medium"
            >
              + Add Rule
            </button>
          </div>

          {rulesLoading ? (
            <div className="text-center py-8 text-sm text-slate-400">
              Loading…
            </div>
          ) : rules.length === 0 && !addingRule ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
              <p className="text-sm text-slate-400 mb-3">
                No rules loaded yet.
              </p>
              <button
                onClick={loadRules}
                className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Load Rules
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) =>
                editingRule === rule.id ? (
                  <div
                    key={rule.id}
                    className="bg-white rounded-xl border border-orange-200 p-4 space-y-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          Priority
                        </label>
                        <input
                          type="number"
                          value={editDraft.priority}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              priority: e.target.value,
                            }))
                          }
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          Subject keywords
                        </label>
                        <input
                          type="text"
                          value={editDraft.subject}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              subject: e.target.value,
                            }))
                          }
                          placeholder="invoice, payment, billing"
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          Body keywords
                        </label>
                        <input
                          type="text"
                          value={editDraft.body}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              body: e.target.value,
                            }))
                          }
                          placeholder="urgent, deadline, overdue"
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          From address(es)
                        </label>
                        <input
                          type="text"
                          value={editDraft.from}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              from: e.target.value,
                            }))
                          }
                          placeholder="client@company.com, other@firm.com"
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          To address(es)
                        </label>
                        <input
                          type="text"
                          value={editDraft.to}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, to: e.target.value }))
                          }
                          placeholder="support@mycompany.com"
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          From sender list
                        </label>
                        <select
                          value={editDraft.from_in_list}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              from_in_list: e.target.value,
                            }))
                          }
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                        >
                          <option value="">— none —</option>
                          {senderLists.map((l) => (
                            <option key={l.name} value={l.name}>
                              {l.name} ({l.address_count} addresses)
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">
                      Comma-separate multiple values. Leave all blank for a
                      catch-all rule.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={saveRule}
                        className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 font-medium"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingRule(null)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={rule.id}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        P{rule.priority}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {conditionSummary(rule.conditions)}
                        </p>
                        <p className="text-xs text-slate-400">
                          → Draft via Knowledge Base
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setEditingRule(rule.id);
                          setEditDraft({
                            priority: rule.priority,
                            subject: joinList(
                              rule.conditions?.subject_contains,
                            ),
                            body: joinList(rule.conditions?.body_contains),
                            from: joinList(rule.conditions?.from_address),
                            to: joinList(rule.conditions?.to_address),
                            from_in_list: rule.conditions?.from_in_list || "",
                          });
                        }}
                        className="text-xs text-slate-400 hover:text-slate-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteRule(rule.id)}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => toggleRule(rule.id, !rule.enabled)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${
                          rule.enabled ? "bg-green-500" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            rule.enabled ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

          {addingRule && (
            <div className="bg-white rounded-xl border border-orange-200 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">New Rule</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Priority
                  </label>
                  <input
                    type="number"
                    value={newRuleDraft.priority}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({
                        ...d,
                        priority: e.target.value,
                      }))
                    }
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Subject keywords
                  </label>
                  <input
                    type="text"
                    value={newRuleDraft.subject}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({
                        ...d,
                        subject: e.target.value,
                      }))
                    }
                    placeholder="invoice, payment, billing"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Body keywords
                  </label>
                  <input
                    type="text"
                    value={newRuleDraft.body}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({ ...d, body: e.target.value }))
                    }
                    placeholder="urgent, deadline, overdue"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    From address(es)
                  </label>
                  <input
                    type="text"
                    value={newRuleDraft.from}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({ ...d, from: e.target.value }))
                    }
                    placeholder="client@company.com, other@firm.com"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    To address(es)
                  </label>
                  <input
                    type="text"
                    value={newRuleDraft.to}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({ ...d, to: e.target.value }))
                    }
                    placeholder="support@mycompany.com"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    From sender list
                  </label>
                  <select
                    value={newRuleDraft.from_in_list}
                    onChange={(e) =>
                      setNewRuleDraft((d) => ({
                        ...d,
                        from_in_list: e.target.value,
                      }))
                    }
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  >
                    <option value="">— none —</option>
                    {senderLists.map((l) => (
                      <option key={l.name} value={l.name}>
                        {l.name} ({l.address_count} addresses)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Comma-separate multiple values. Leave all blank for a catch-all
                rule.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={createRule}
                  className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 font-medium"
                >
                  Add Rule
                </button>
                <button
                  onClick={() => {
                    setAddingRule(false);
                    setNewRuleDraft({
                      priority: 100,
                      subject: "",
                      body: "",
                      from: "",
                      to: "",
                      from_in_list: "",
                    });
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Sender Lists ── */}
          <div className="mt-6 pt-5 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Sender Lists
                </p>
                <p className="text-xs text-slate-400">
                  Named address lists you can reference in routing rules.
                  Replace the list weekly without editing rules.
                </p>
              </div>
              <button
                onClick={() => setAddingList(true)}
                className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium"
              >
                + New List
              </button>
            </div>

            {senderListsLoading ? (
              <div className="text-center py-4 text-xs text-slate-400">
                Loading…
              </div>
            ) : senderLists.length === 0 && !addingList ? (
              <p className="text-xs text-slate-400 py-2">
                No sender lists yet.
              </p>
            ) : (
              <div className="space-y-2">
                {senderLists.map((list) =>
                  editingList === list.name ? (
                    <div
                      key={list.name}
                      className="bg-white rounded-xl border border-orange-200 p-4 space-y-3"
                    >
                      <p className="text-sm font-medium text-slate-700">
                        {list.name}
                      </p>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          Addresses — one per line or comma-separated
                        </label>
                        <textarea
                          value={listAddressDraft}
                          onChange={(e) => setListAddressDraft(e.target.value)}
                          rows={6}
                          placeholder={"customer@example.com\nclient@firm.com"}
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 font-mono resize-y"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveSenderList(list.name)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingList(null)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={list.name}
                      className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {list.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {list.address_count} address
                          {list.address_count !== 1 ? "es" : ""} · updated{" "}
                          {new Date(list.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => startEditList(list.name)}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteSenderList(list.name)}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

            {addingList && (
              <div className="bg-white rounded-xl border border-orange-200 p-4 space-y-3 mt-2">
                <p className="text-sm font-medium text-slate-700">
                  New Sender List
                </p>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    List name
                  </label>
                  <input
                    type="text"
                    value={newListDraft.name}
                    onChange={(e) =>
                      setNewListDraft((d) => ({ ...d, name: e.target.value }))
                    }
                    placeholder="existing_customers"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">
                    Addresses — one per line or comma-separated
                  </label>
                  <textarea
                    value={newListDraft.addresses}
                    onChange={(e) =>
                      setNewListDraft((d) => ({
                        ...d,
                        addresses: e.target.value,
                      }))
                    }
                    rows={6}
                    placeholder={"customer@example.com\nclient@firm.com"}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 font-mono resize-y"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createSenderList}
                    disabled={!newListDraft.name.trim()}
                    className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 font-medium disabled:opacity-50"
                  >
                    Create List
                  </button>
                  <button
                    onClick={() => {
                      setAddingList(false);
                      setNewListDraft({ name: "", addresses: "" });
                    }}
                    className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "drafts" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">
            Recent draft log coming soon. Drafts appear in your M365 Drafts
            folder in real-time.
          </p>
        </div>
      )}

      {activeTab === "console" && <EmailReviewConsole facility={facility} />}

      {activeTab === "eval" && <EmailIntelSettings facility={facility} />}
    </div>
  );
}
